/** Default sample photo used when a set has no uploaded images. */
export const DEFAULT_SET_PHOTO_URL = '/images/default-set-photo.jpg';

/** Category-keyed sample product photos (bundled under client/public/images). */
export const SAMPLE_SET_PHOTO_BY_CATEGORY: Record<string, string> = {
  electronics: '/images/set-sample-electronics.jpg',
  robotics: '/images/set-sample-electronics.jpg',
  coding: '/images/set-sample-electronics.jpg',
  wood: '/images/set-sample-woodwork.jpg',
  woodwork: '/images/set-sample-woodwork.jpg',
  woodworking: '/images/set-sample-woodwork.jpg',
  crafts: '/images/set-sample-woodwork.jpg',
  mechanics: '/images/set-sample-mechanical.jpg',
  mechanical: '/images/set-sample-mechanical.jpg',
  engineering: '/images/set-sample-mechanical.jpg',
  physics: '/images/set-sample-mechanical.jpg',
  games: '/images/set-sample-games.jpg',
  game: '/images/set-sample-games.jpg',
  puzzle: '/images/set-sample-games.jpg',
  education: '/images/set-sample-games.jpg',
};

type MediaLike = {
  file_url?: string | null;
  is_featured?: boolean | number | string | null;
  file_type?: string | null;
  mime_type?: string | null;
};

type SetLike = {
  media?: MediaLike[] | null;
  media_count?: number | null;
  category?: string | null;
};

export function isFeaturedMedia(media: MediaLike | null | undefined): boolean {
  const v = media?.is_featured;
  return v === true || v === 1 || v === '1' || v === 't' || v === 'true';
}

export function getSetPhotos(set: SetLike): MediaLike[] {
  return Array.isArray(set.media) ? set.media : [];
}

/** Pick a bundled sample photo URL for a set category (falls back to default). */
export function getSampleSetPhotoUrl(category?: string | null): string {
  const key = String(category || '')
    .trim()
    .toLowerCase();
  if (!key) return DEFAULT_SET_PHOTO_URL;

  if (SAMPLE_SET_PHOTO_BY_CATEGORY[key]) {
    return SAMPLE_SET_PHOTO_BY_CATEGORY[key];
  }

  // Fuzzy match: "Electronics Kit" / "Wood craft" etc.
  for (const [token, url] of Object.entries(SAMPLE_SET_PHOTO_BY_CATEGORY)) {
    if (key.includes(token)) return url;
  }

  return DEFAULT_SET_PHOTO_URL;
}

/** Prefer featured/master image, else first media item, else a category sample. */
export function getSetDisplayPhotoUrl(set: SetLike): string {
  const photos = getSetPhotos(set);
  if (!photos.length) return getSampleSetPhotoUrl(set.category);

  const featured = photos.find((m) => isFeaturedMedia(m)) || photos[0];
  const url = featured?.file_url;
  if (typeof url === 'string' && url.trim()) return url;
  return getSampleSetPhotoUrl(set.category);
}

export function getSetMasterPhoto(set: SetLike): MediaLike | null {
  const photos = getSetPhotos(set);
  if (!photos.length) return null;
  return photos.find((m) => isFeaturedMedia(m)) || photos[0] || null;
}

/** Fetch a bundled sample photo as a File for upload via mediaApi. */
export async function fetchSampleSetPhotoFile(category?: string | null): Promise<File> {
  const url = getSampleSetPhotoUrl(category);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to load sample photo');
  }
  const blob = await response.blob();
  const type = blob.type || 'image/jpeg';
  const basename = url.split('/').pop() || 'sample-set-photo.jpg';
  return new File([blob], basename, { type });
}
